import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaMoneyBillWave,
  FaFileSignature,
  FaUpload,
  FaXmark,
  FaClipboardCheck,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";

const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
    ],
  },
  {
    id: 2,
    title: "Employee\nInformation",
    mobileTitle: "Employee\nInformation",
    icon: FaUser,
    fields: [
      "employeeName",
      "employeeId",
      "employeePhone",
      "employeeEmail",
      "residentialAddress",
      "currentDesignation",
      "currentDepartment",
      "workLocation",
      "reportingManager",
    ],
  },
  {
    id: 3,
    title: "Termination\nDetails",
    mobileTitle: "Termination\nDetails",
    icon: FaClipboardCheck,
    fields: [
      "terminationLetterDate",
      "terminationType",
      "terminationCategory",
      "reasonForTermination",
      "detailedExplanation",
      "relevantClause",
      "previousCommunication",
      "employeeExplanation",
    ],
  },
  {
    id: 4,
    title: "Notice & Final\nWorking Date",
    mobileTitle: "Notice & Final\nWorking Date",
    icon: FaBriefcase,
    fields: [
      "terminationEffectiveDate",
      "lastWorkingDate",
      "noticePeriod",
      "noticePeriodStatus",
      "noticeStartDate",
      "noticeEndDate",
      "noticePayAmount",
      "dutiesDuringNotice",
    ],
  },
  {
    id: 5,
    title: "Final\nSettlement",
    mobileTitle: "Final\nSettlement",
    icon: FaMoneyBillWave,
    fields: [
      "salaryPayableUpTo",
      "pendingSalary",
      "leaveEncashment",
      "bonusOrIncentive",
      "gratuityEligibility",
      "noticePayAdjustment",
      "loanOrAdvanceRecovery",
      "expenseReimbursement",
      "fullAndFinalStatus",
      "expectedSettlementDate",
    ],
  },
  {
    id: 6,
    title: "Handover &\nAuthorisation",
    mobileTitle: "Handover &\nAuthorisation",
    icon: FaFileSignature,
    fields: [
      "handoverRequired",
      "handoverRecipient",
      "assetReturnDeadline",
      "systemAccessDeactivationDate",
      "clearanceStatus",
      "issuedBy",
      "issuedByDesignation",
      "approvedBy",
      "referenceNumber",
      "acknowledgementRequired",
    ],
  },
];

const defaultValues = {
  // Company
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,

  // Employee
  employeeName: "",
  employeeId: "",
  employeePhone: "",
  employeeEmail: "",
  residentialAddress: "",
  currentDesignation: "",
  currentDepartment: "",
  workLocation: "",
  reportingManager: "",

  // Termination Details
  terminationLetterDate: "",
  terminationType: "",
  terminationCategory: "",
  reasonForTermination: "",
  detailedExplanation: "",
  relevantClause: "",
  previousCommunication: "",
  employeeExplanation: "",

  // Notice and Final Working Date
  terminationEffectiveDate: "",
  lastWorkingDate: "",
  noticePeriod: "",
  noticePeriodStatus: "",
  noticeStartDate: "",
  noticeEndDate: "",
  noticePayAmount: "",
  dutiesDuringNotice: "",

  // Final Settlement
  salaryPayableUpTo: "",
  pendingSalary: "",
  leaveEncashment: "",
  bonusOrIncentive: "",
  gratuityEligibility: "",
  noticePayAdjustment: "",
  loanOrAdvanceRecovery: "",
  expenseReimbursement: "",
  fullAndFinalStatus: "",
  expectedSettlementDate: "",

  // Handover and Authorisation
  handoverRequired: "",
  handoverRecipient: "",
  assetReturnDeadline: "",
  systemAccessDeactivationDate: "",
  clearanceStatus: "",
  issuedBy: "",
  issuedByDesignation: "",
  approvedBy: "",
  referenceNumber: "",
  acknowledgementRequired: "",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[10px] text-red-500">{error.message}</p>
      )}
    </div>
  );
};

const parseDateValue = (value) => {
  if (!value) return null;

  const [year, month, day] = String(value).split("-").map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const formatDateValue = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const DateField = ({ name, label, required, control, errors }) => (
  <FormField label={label} required={required} error={errors[name]}>
    <Controller
      name={name}
      control={control}
      rules={
        required
          ? {
              required: `${label} is required`,
            }
          : {}
      }
      render={({ field }) => (
        <DatePicker
          selected={parseDateValue(field.value)}
          onChange={(date) => {
            field.onChange(formatDateValue(date));
          }}
          onBlur={field.onBlur}
          dateFormat="yyyy-MM-dd"
          showPopperArrow={false}
          isClearable
          placeholderText="Select date"
          showIcon
          toggleCalendarOnIconClick
          icon={<CalendarIcon className="h-4 w-4" />}
          className={`${inputClass} h-10`}
          wrapperClassName="w-full"
          popperClassName="payslip-datepicker-popper react-popper-offset"
          calendarClassName="payslip-datepicker"
          popperPlacement="top-start"
          portalId="datepicker-portal"
        />
      )}
    />
  </FormField>
);

const BASE_URL = import.meta.env.VITE_BASE_URL;

const TerminationLetterGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();

  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    // Company logo is manually handled
    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });

      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");
  };

  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = async (data) => {
    try {
      if (isGenerating) return;

      setIsGenerating(true);

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) {
            formData.append("companyLogo", value);
          }
        } else {
          formData.append(key, value ?? "");
        }
      });

      const response = await axios.post(
        `${BASE_URL}/generate-terminationletter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();

        console.error("Backend returned:", text);

        throw new Error("Backend did not return a Termination Letter PDF");
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Termination-Letter-${data.employeeName || "Employee"}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.open(url, "_blank");

      // reset(defaultValues);

      setLogoPreview("");

      setActiveStep(0);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Termination letter generation failed:", error);

      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        try {
          const errorData = JSON.parse(text);

          alert(errorData?.message || "Failed to generate Termination Letter");
        } catch {
          alert("Failed to generate Termination Letter");
        }
      } else {
        alert(error?.message || "Failed to generate Termination Letter");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section>
      <form className="w-full">
        <div className="mx-auto grid w-full grid-cols-1 gap-2 xl:grid-cols-[1.9fr_1fr]">
          {/* ================= FORM ================= */}
          <div className="min-w-0 rounded-xl border border-white  shadow-sm backdrop-blur-2xl p-4">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (valid) {
                        setActiveStep(index);
                      }
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-2 py-2.5 text-center transition                       
          sm:min-w-0 
          ${
            active
              ? " bg-[#10324F] text-white shadow-sm"
              : "border border-white  text-[#10324F] bg-linear-to-b from-white/60 to-[#D1DFEA]/30  shadow-sm hover:bg-white/70"
          }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <EmployeeInformation register={register} errors={errors} />
              )}

              {activeStep === 2 && (
                <TerminationDetails
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <NoticeFinalWorkingDate
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 4 && (
                <FinalSettlement
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 5 && (
                <HandoverAuthorisation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}
            </div>

            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Termination Letter"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <TerminationLetterPreview values={values} logoPreview={logoPreview} />
        </div>
      </form>
    </section>
  );
};

export default TerminationLetterGeneratorForm;

const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Company Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Company Name" required error={errors.companyName}>
          <input
            {...register("companyName", {
              required: "Company name is required",
              minLength: {
                value: 2,
                message: "Enter a valid company name",
              },
            })}
            className={inputClass}
            placeholder="Enter Company name"
          />
        </FormField>

        <FormField label="Company Email" required error={errors.companyEmail}>
          <input
            type="email"
            {...register("companyEmail", {
              required: "Company email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Email"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.companyPhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("companyPhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Website" error={errors.companyWebsite}>
          <input
            {...register("companyWebsite", {
              required: "Company website is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Enter valid website",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Website"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Company Address"
            required
            error={errors.companyAddress}
          >
            <input
              {...register("companyAddress", {
                required: "Company address is required",
              })}
              className={inputClass}
              placeholder="Enter Company Address"
            />
          </FormField>
        </div>

        <div className="sm:col-span-2">
          <FormField label="Company Logo" required error={errors.companyLogo}>
            {logoPreview ? (
              <div className="w-60">
                <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                  <img
                    src={logoPreview}
                    alt="Company logo"
                    className="h-14 max-w-55 object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                    aria-label="Remove logo"
                  >
                    <FaXmark className="text-xs" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
                <div className="text-center text-[10px] text-[#10324F]">
                  <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                  Upload Logo
                </div>

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </FormField>
        </div>
      </div>
    </div>
  );
};

const EmployeeInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Employee Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Employee Full Name"
          required
          error={errors.employeeName}
        >
          <input
            {...register("employeeName", {
              required: "Employee name is required",
            })}
            className={inputClass}
            placeholder="Enter name"
          />
        </FormField>

        <FormField label="Employee ID" required error={errors.employeeId}>
          <input
            {...register("employeeId", {
              required: "Employee ID is required",
            })}
            className={inputClass}
            placeholder="Enter ID"
          />
        </FormField>

        <FormField label="Phone Number" error={errors.employeePhone}>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            {...register("employeePhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Email Address" error={errors.employeeEmail}>
          <input
            type="email"
            {...register("employeeEmail", {
              required: "Employee email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Email"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Residential address"
            required
            error={errors.residentialAddress}
          >
            <input
              {...register("residentialAddress", {
                required: "Residential address is required",
              })}
              className={inputClass}
              placeholder="Enter Address"
            />
          </FormField>
        </div>

        <FormField
          label="Current designation"
          required
          error={errors.currentDesignation}
        >
          <input
            {...register("currentDesignation", {
              required: "Current designation is required",
            })}
            className={inputClass}
            placeholder="Enter Final designation"
          />
        </FormField>

        <FormField label="Current Department" error={errors.currentDepartment}>
          <input
            {...register("currentDepartment")}
            className={inputClass}
            placeholder="Enter Department"
          />
        </FormField>

        <FormField label="Work Location" error={errors.workLocation}>
          <input
            {...register("workLocation")}
            className={inputClass}
            placeholder="Enter Work Location"
          />
        </FormField>

        <FormField
          label="Current reporting manager"
          error={errors.reportingManager}
        >
          <input
            {...register("reportingManager")}
            className={inputClass}
            placeholder="Select Reporting Manager"
          />
        </FormField>
      </div>
    </div>
  );
};

const TerminationDetails = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Termination Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          name="terminationLetterDate"
          label="Termination letter date"
          required
          control={control}
          errors={errors}
        />

        <FormField
          label="Termination type"
          required
          error={errors.terminationType}
        >
          <select
            {...register("terminationType", {
              required: "Termination type is required",
            })}
            className={inputClass}
          >
            <option value="">Select Termination type</option>
            <option value="Termination with Notice">
              Termination with Notice
            </option>
            <option value="Termination without Notice">
              Termination without Notice
            </option>
            <option value="Termination for Cause">Termination for Cause</option>
            <option value="Termination during Probation">
              Termination during Probation
            </option>
            <option value="End of Contract">End of Contract</option>
            <option value="Redundancy">Redundancy</option>
          </select>
        </FormField>

        <FormField
          label="Termination category"
          required
          error={errors.terminationCategory}
        >
          <select
            {...register("terminationCategory", {
              required: "Termination category is required",
            })}
            className={inputClass}
          >
            <option value="">Select Category</option>
            <option value="Performance">Performance</option>
            <option value="Misconduct">Misconduct</option>
            <option value="Attendance">Attendance</option>
            <option value="Policy Violation">Policy Violation</option>
            <option value="Restructuring">Restructuring</option>
            <option value="Redundancy">Redundancy</option>
            <option value="End of Contract">End of Contract</option>
            <option value="Other">Other</option>
          </select>
        </FormField>

        <FormField
          label="Reason for termination"
          required
          error={errors.reasonForTermination}
        >
          <input
            {...register("reasonForTermination", {
              required: "Reason for termination is required",
            })}
            className={inputClass}
            placeholder="Select Reason"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Detailed explanation"
            error={errors.detailedExplanation}
          >
            <textarea
              {...register("detailedExplanation")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter Explanation"
            />
          </FormField>
        </div>

        <FormField
          label="Relevant appointment or policy clause"
          error={errors.relevantClause}
        >
          <input
            {...register("relevantClause")}
            className={inputClass}
            placeholder="Enter details"
          />
        </FormField>

        <FormField
          label="Previous warning or communication"
          error={errors.previousCommunication}
        >
          <select {...register("previousCommunication")} className={inputClass}>
            <option value="">Enter Details</option>
            <option value="None">None</option>
            <option value="Verbal Warning">Verbal Warning</option>
            <option value="Written Warning">Written Warning</option>
            <option value="Final Written Warning">Final Written Warning</option>
            <option value="Show Cause Notice">Show Cause Notice</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Employee explanation (if any)"
            error={errors.employeeExplanation}
          >
            <textarea
              {...register("employeeExplanation")}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Enter Employee explanation"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const NoticeFinalWorkingDate = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Notice and Final Working Date
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          name="terminationEffectiveDate"
          label="Termination effective date"
          required
          control={control}
          errors={errors}
        />

        <DateField
          name="lastWorkingDate"
          label="Last working date"
          required
          control={control}
          errors={errors}
        />

        <FormField label="Notice period" required error={errors.noticePeriod}>
          <select
            {...register("noticePeriod", {
              required: "Notice period is required",
            })}
            className={inputClass}
          >
            <option value="">Select Notice Period</option>
            <option value="No Notice Period">No Notice Period</option>
            <option value="15 Days">15 Days</option>
            <option value="30 Days">30 Days</option>
            <option value="45 Days">45 Days</option>
            <option value="60 Days">60 Days</option>
            <option value="90 Days">90 Days</option>
          </select>
        </FormField>

        <FormField
          label="Notice-period status"
          required
          error={errors.noticePeriodStatus}
        >
          <select
            {...register("noticePeriodStatus", {
              required: "Notice-period status is required",
            })}
            className={inputClass}
          >
            <option value="">Select Status</option>
            <option value="Served">Served</option>
            <option value="Partially Served">Partially Served</option>
            <option value="Waived">Waived</option>
            <option value="Payment in Lieu of Notice">
              Payment in Lieu of Notice
            </option>
          </select>
        </FormField>

        <DateField
          name="noticeStartDate"
          label="Notice start date"
          control={control}
          errors={errors}
        />

        <DateField
          name="noticeEndDate"
          label="Notice end date"
          control={control}
          errors={errors}
        />

        <FormField label="Notice pay amount" error={errors.noticePayAmount}>
          <input
            type="text"
            inputMode="numeric"
            {...register("noticePayAmount", {
              pattern: {
                value: /^[0-9]+$/,
                message: "Enter a valid amount",
              },
            })}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField
          label="Duties during notice period"
          error={errors.dutiesDuringNotice}
        >
          <input
            {...register("dutiesDuringNotice")}
            className={inputClass}
            placeholder="Enter Duties"
          />
        </FormField>
      </div>
    </div>
  );
};

const FinalSettlement = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Final Settlement
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          name="salaryPayableUpTo"
          label="Salary payable up to"
          control={control}
          errors={errors}
        />

        <FormField label="Pending salary" error={errors.pendingSalary}>
          <input
            type="text"
            inputMode="numeric"
            {...register("pendingSalary")}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField label="Leave encashment" error={errors.leaveEncashment}>
          <input
            type="text"
            inputMode="numeric"
            {...register("leaveEncashment")}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField label="Bonus or incentive" error={errors.bonusOrIncentive}>
          <input
            type="text"
            inputMode="numeric"
            {...register("bonusOrIncentive")}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField
          label="Gratuity eligibility"
          error={errors.gratuityEligibility}
        >
          <select {...register("gratuityEligibility")} className={inputClass}>
            <option value="">Select status</option>
            <option value="Eligible">Eligible</option>
            <option value="Not Eligible">Not Eligible</option>
            <option value="Under Review">Under Review</option>
          </select>
        </FormField>

        <FormField
          label="Notice-pay adjustment"
          error={errors.noticePayAdjustment}
        >
          <input
            type="text"
            inputMode="numeric"
            {...register("noticePayAdjustment")}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField
          label="Loan or advance recovery"
          error={errors.loanOrAdvanceRecovery}
        >
          <input
            type="text"
            inputMode="numeric"
            {...register("loanOrAdvanceRecovery")}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField
          label="Expense reimbursement"
          error={errors.expenseReimbursement}
        >
          <input
            type="text"
            inputMode="numeric"
            {...register("expenseReimbursement")}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(/\D/g, "");
            }}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField
          label="Full and final settlement status"
          error={errors.fullAndFinalStatus}
        >
          <select {...register("fullAndFinalStatus")} className={inputClass}>
            <option value="">Select Status</option>
            <option value="Completed">Completed</option>
            <option value="In Process">In Process</option>
            <option value="Pending">Pending</option>
          </select>
        </FormField>

        <DateField
          name="expectedSettlementDate"
          label="Expected settlement date"
          control={control}
          errors={errors}
        />
      </div>
    </div>
  );
};

const HandoverAuthorisation = ({ register, errors, control }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Handover and Authorisation
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Handover Required" error={errors.handoverRequired}>
          <select {...register("handoverRequired")} className={inputClass}>
            <option value="">Yes / No</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </FormField>

        <FormField label="Handover Recipient" error={errors.handoverRecipient}>
          <input
            {...register("handoverRecipient")}
            className={inputClass}
            placeholder="Enter Recipient name"
          />
        </FormField>

        <DateField
          name="assetReturnDeadline"
          label="Asset-Return Deadline"
          control={control}
          errors={errors}
        />

        <DateField
          name="systemAccessDeactivationDate"
          label="System Access Deactivation Date"
          control={control}
          errors={errors}
        />

        <FormField label="Clearance Status" error={errors.clearanceStatus}>
          <select {...register("clearanceStatus")} className={inputClass}>
            <option value="">Select Status</option>
            <option value="Cleared">Cleared</option>
            <option value="Pending">Pending</option>
            <option value="Not Applicable">Not Applicable</option>
          </select>
        </FormField>

        <FormField label="Issued By" required error={errors.issuedBy}>
          <input
            {...register("issuedBy", {
              required: "Issued by is required",
            })}
            className={inputClass}
            placeholder="Enter Name"
          />
        </FormField>

        <FormField
          label="Issued By Designation"
          required
          error={errors.issuedByDesignation}
        >
          <input
            {...register("issuedByDesignation", {
              required: "Issued by designation is required",
            })}
            className={inputClass}
            placeholder="Enter Designation"
          />
        </FormField>

        <FormField label="Approved By" error={errors.approvedBy}>
          <input
            {...register("approvedBy")}
            className={inputClass}
            placeholder="Enter name"
          />
        </FormField>

        <FormField
          label="Reference Number"
          required
          error={errors.referenceNumber}
        >
          <input
            {...register("referenceNumber", {
              required: "Reference number is required",
            })}
            className={inputClass}
            placeholder="Enter Reference number"
          />
        </FormField>

        <FormField
          label="Employee Acknowledgement Required"
          error={errors.acknowledgementRequired}
        >
          <select
            {...register("acknowledgementRequired")}
            className={inputClass}
          >
            <option value="">Yes / No</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </FormField>
      </div>
    </div>
  );
};

const TerminationLetterPreview = ({ values, logoPreview }) => {
  const formatDate = (date) => {
    if (!date) return "24 August, 2026";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return date;

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-xl border border-white shadow-sm backdrop-blur-2xl p-4">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      <div className="mx-auto w-full bg-white">
        <Ribbon position="top" />

        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-8 max-w-25 object-contain"
              />
            ) : (
              <span className="text-lg text-[#10324F]">
                {values.companyName || "Company"}
              </span>
            )}
          </div>

          {/* Heading */}
          <div className="mt-3 text-center">
            <p className="text-xs font-bold text-[#10324F]">
              TERMINATION LETTER
            </p>
          </div>

          {/* Reference */}
          {values.referenceNumber && (
            <div className="mt-3 text-xs text-[#10324F]">
              <p>
                <strong>Reference No:</strong> {values.referenceNumber}
              </p>
            </div>
          )}

          {/* Recipient */}
          <div className="mt-4 text-xs leading-4 text-[#10324F]">
            <p className="font-semibold">To:</p>

            <p>{values.employeeName || "Employee Name"}</p>

            {values.employeeId && <p>Employee ID: {values.employeeId}</p>}

            <p>{values.residentialAddress || "Employee Address"}</p>
          </div>

          {/* Body */}
          <div className="mt-3 text-xs leading-4 text-[#10324F]">
            <p>Dear {values.employeeName || "Employee Name"},</p>

            <p className="mt-3">
              This letter is to inform you that your employment with{" "}
              <strong>{values.companyName || "Company Name"}</strong> as{" "}
              <strong>{values.currentDesignation || "Designation"}</strong>
              {values.currentDepartment && (
                <>
                  {" "}
                  in the <strong>{values.currentDepartment}</strong> department
                </>
              )}{" "}
              is being terminated
              {values.terminationType && (
                <>
                  {" "}
                  by way of <strong>{values.terminationType}</strong>
                </>
              )}
              , effective{" "}
              <strong>{formatDate(values.terminationEffectiveDate)}</strong>.
            </p>

            <p className="mt-3">
              Your last working day with the organisation will be{" "}
              <strong>{formatDate(values.lastWorkingDate)}</strong>.
            </p>

            {values.reasonForTermination && (
              <p className="mt-3">
                Reason for termination:{" "}
                <strong>{values.reasonForTermination}</strong>
                {values.terminationCategory && (
                  <> ({values.terminationCategory})</>
                )}
                .
              </p>
            )}

            {values.detailedExplanation && (
              <p className="mt-3">{values.detailedExplanation}</p>
            )}

            {values.relevantClause && (
              <p className="mt-3">
                This action is taken under {values.relevantClause}.
              </p>
            )}

            {values.previousCommunication &&
              values.previousCommunication !== "None" && (
                <p className="mt-3">
                  Previous communication on record:{" "}
                  {values.previousCommunication}.
                </p>
              )}

            {values.employeeExplanation && (
              <p className="mt-3">{values.employeeExplanation}</p>
            )}

            {values.noticePeriod && (
              <p className="mt-3">
                Notice period: <strong>{values.noticePeriod}</strong>
                {values.noticePeriodStatus && (
                  <> ({values.noticePeriodStatus})</>
                )}
                {values.noticeStartDate && (
                  <>
                    {" "}
                    from {formatDate(values.noticeStartDate)}
                    {values.noticeEndDate && (
                      <> to {formatDate(values.noticeEndDate)}</>
                    )}
                  </>
                )}
                .
              </p>
            )}

            {values.dutiesDuringNotice && (
              <p className="mt-3">{values.dutiesDuringNotice}</p>
            )}

            {(values.fullAndFinalStatus || values.expectedSettlementDate) && (
              <p className="mt-3">
                Full and final settlement status:{" "}
                <strong>{values.fullAndFinalStatus || "In Process"}</strong>
                {values.expectedSettlementDate && (
                  <>, expected by {formatDate(values.expectedSettlementDate)}</>
                )}
                .
              </p>
            )}

            {values.handoverRequired === "Yes" && (
              <p className="mt-3">
                You are required to complete a full handover
                {values.handoverRecipient && (
                  <> to {values.handoverRecipient}</>
                )}
                {values.assetReturnDeadline && (
                  <>
                    {" "}
                    and return all company assets by{" "}
                    {formatDate(values.assetReturnDeadline)}
                  </>
                )}
                .
              </p>
            )}

            {values.systemAccessDeactivationDate && (
              <p className="mt-3">
                System access will be deactivated on{" "}
                {formatDate(values.systemAccessDeactivationDate)}.
              </p>
            )}

            <p className="mt-3">
              We thank you for your services and wish you well in your future
              endeavours.
            </p>
          </div>

          {/* Date */}
          <div className="mt-3 text-xs text-[#10324F]">
            <p>Date: {formatDate(values.terminationLetterDate)}</p>

            {values.approvedBy && (
              <p className="mt-1">Approved by: {values.approvedBy}</p>
            )}
          </div>

          {/* Signature */}
          <div className="mt-4 text-xs text-[#10324F]">
            <p>Sincerely,</p>

            <p className="mt-3 font-semibold">
              {values.issuedBy || "Authorised Signatory"}
            </p>

            <p>{values.issuedByDesignation || "HR Manager"}</p>

            <p>{values.companyName || "Company Name"}</p>
          </div>

          {/* Acknowledgement */}
          {values.acknowledgementRequired === "Yes" && (
            <div className="mt-4 text-xs leading-4 text-[#10324F]">
              <p className="font-semibold">Employee Acknowledgement</p>

              <p className="mt-1">
                I acknowledge receipt of this termination letter and understand
                its contents.
              </p>

              <p className="mt-3">Signature: ____________ Date: ____________</p>
            </div>
          )}

          <div className="mt-6 text-center text-xs text-[#10324F]">
            Powered by Waggex
          </div>
        </div>

        <Ribbon position="bottom" />
      </div>
    </div>
  );
};

const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      {/* Green base */}
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{
          transform: isTop ? "none" : "scaleX(-1) scaleY(-1)",
        }}
      >
        {/* Magenta ribbon */}
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        {/* White cut / separation */}
        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{
            clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)",
          }}
        />
      </div>
    </div>
  );
};
